export type Surface = 'floor' | 'ceiling';

/**
 * Cosmetic-only section labels. Each maps to a tinted band the renderer
 * draws across the playfield - none of these affect engine rules. The
 * field gives authors a way to colour-code structural parts of the song
 * (verse / chorus / bridge / drop) without implying any gameplay behaviour.
 */
export type SectionKind = 'verse' | 'chorus' | 'bridge' | 'drop';

export type Difficulty = 1 | 2 | 3 | 4 | 5;

/**
 * Per-track density variant — orthogonal to {@link Difficulty}, which is the
 * intrinsic *song* tier (a tier-1 song still gets all three variants, just
 * with fewer baseline pads). Authors of builtin charts produce one chart per
 * variant by scaling their `selectBeats` filters with {@link VARIANT_TUNING}.
 *
 * Naming convention: the slug for the normal variant is the song's bare slug
 * (e.g. `invincible`); harder variants suffix the slug (`invincible-hard`,
 * `invincible-extreme`). This keeps existing leaderboard / progression rows
 * — which key on the bare slug — pinned to the normal variant with no
 * backfill, while the harder variants live in fresh DB rows.
 */
export type Variant = 'normal' | 'hard' | 'extreme';

export const VARIANTS: ReadonlyArray<Variant> = ['normal', 'hard', 'extreme'];

/**
 * Circles mode extends the shared three-tier ladder with a fourth,
 * hardest tier. This is **circles-only**: Pulse, Guitar, Piano and Waves
 * ship exactly the three {@link Variant} tiers, so widening the shared
 * {@link Variant}/{@link VARIANTS} would force every mode's
 * `Record<Variant, …>` catalogue to grow a tier it has no data for. Keep
 * the standard set at three and let circles widen its own surface here.
 *
 * Ladder (easiest → hardest): normal · hard · extreme · insane. Circles'
 * `normal` is a slider-free on-ramp — every osu! slider collapses to a
 * single click — so `hard` is the first tier that contains sliders.
 */
export type CirclesVariant = Variant | 'insane';

export const CIRCLES_VARIANTS: ReadonlyArray<CirclesVariant> = [
  'normal',
  'hard',
  'extreme',
  'insane',
];

/**
 * Density tuning per variant. Chart authors multiply their `minIntensity`
 * and `minSpacing` arguments by these factors when building each variant.
 *
 * - `intensityFactor < 1` lowers the audio-onset strength threshold so
 *   weaker hits (ghost kicks, shimmer snares) become eligible pads.
 * - `spacingFactor < 1` lowers the minimum-spacing floor so denser
 *   clusters of pads can coexist.
 *
 * The numbers were picked so Hard roughly doubles the pad count of Normal
 * on a typical chart and Extreme roughly triples it, while still respecting
 * the "every required pad lands on a real audio onset" invariant
 * (`PAD_ONSET_TOLERANCE_MS`) — the lower thresholds simply recruit more of
 * the existing onset list, never invent beats off-grid.
 */
export const VARIANT_TUNING: Record<Variant, { intensityFactor: number; spacingFactor: number }> = {
  normal:  { intensityFactor: 1.0, spacingFactor: 1.0 },
  hard:    { intensityFactor: 0.7, spacingFactor: 0.65 },
  extreme: { intensityFactor: 0.4, spacingFactor: 0.4 },
};

/**
 * Human-readable label for a variant. UI surfaces (homepage selector, play
 * badge) use this so we don't sprinkle title-case strings through the
 * codebase.
 */
export function variantLabel(variant: CirclesVariant): string {
  switch (variant) {
    case 'normal': {
      return 'Normal';
    }
    case 'hard': {
      return 'Hard';
    }
    case 'extreme': {
      return 'Extreme';
    }
    case 'insane': {
      return 'Insane';
    }
  }
}

/**
 * Slug rule: bare slug for normal, suffixed slug for hard/extreme. Inverse
 * of {@link parseVariantSlug}.
 */
export function buildVariantSlug(baseSlug: string, variant: Variant): string {
  if (variant === 'normal') {
    return baseSlug;
  }
  return `${baseSlug}-${variant}`;
}

/**
 * Recover the (baseSlug, variant) pair from a slug. Slugs ending in
 * `-hard` or `-extreme` decode as those variants; anything else is normal.
 * Used by the play page when only the URL slug is available.
 */
export function parseVariantSlug(slug: string): { baseSlug: string; variant: Variant } {
  if (slug.endsWith('-extreme')) {
    return { baseSlug: slug.slice(0, -'-extreme'.length), variant: 'extreme' };
  }
  if (slug.endsWith('-hard')) {
    return { baseSlug: slug.slice(0, -'-hard'.length), variant: 'hard' };
  }
  return { baseSlug: slug, variant: 'normal' };
}

/**
 * Circles slug rule: bare slug for `normal`, suffixed slug for the harder
 * tiers (`-hard` / `-extreme` / `-insane`). Circles-specific twin of
 * {@link buildVariantSlug} that understands the fourth tier.
 */
export function buildCirclesVariantSlug(baseSlug: string, variant: CirclesVariant): string {
  if (variant === 'normal') {
    return baseSlug;
  }
  return `${baseSlug}-${variant}`;
}

/**
 * Recover the (baseSlug, variant) pair from a circles slug. Understands
 * the circles-only `-insane` tier on top of the shared suffixes — the
 * play + browse pages route on the URL slug alone, so they must decode
 * insane correctly (the shared {@link parseVariantSlug} would mis-read
 * `foo-insane` as a bare `normal` slug literally named `foo-insane`).
 */
export function parseCirclesVariantSlug(
  slug: string,
): { baseSlug: string; variant: CirclesVariant } {
  if (slug.endsWith('-insane')) {
    return { baseSlug: slug.slice(0, -'-insane'.length), variant: 'insane' };
  }
  if (slug.endsWith('-extreme')) {
    return { baseSlug: slug.slice(0, -'-extreme'.length), variant: 'extreme' };
  }
  if (slug.endsWith('-hard')) {
    return { baseSlug: slug.slice(0, -'-hard'.length), variant: 'hard' };
  }
  return { baseSlug: slug, variant: 'normal' };
}

export type JudgeVerdict = 'perfect' | 'good' | 'ok' | 'miss';

// Judgement windows in milliseconds. A tap within ±PERFECT lands a perfect
// (full +1 HP, full score), within ±GOOD lands a good (+1 HP, partial
// score), within ±OK lands an ok (mercy band — small partial score, no HP),
// and everything beyond OK falls outside the pad's hit area entirely.
// The renderer paints a translucent halo extending to ±GOOD on each pad
// so players can see the full OK zone without us moving the pad core itself.
//
// OK is only used by modes that opt into it (currently Circles). Pulse
// continues to operate as a two-window system; the OK_WINDOW_MS / OK_SCORE
// constants are present only for modes that pass `okWindowMs` to `judge`.
export const PERFECT_WINDOW_MS = 50;
export const GOOD_WINDOW_MS = 120;
export const OK_WINDOW_MS = 180;
/**
 * Pulse mode uses a tighter good window than the shared 120ms — the
 * approach-ring + horizontal scroll already give the player a strong
 * timing read, so 85ms still feels generous without making it trivial
 * to cover misses with sloppy taps. Circles still uses GOOD_WINDOW_MS.
 */
export const PULSE_GOOD_WINDOW_MS = 85;

export const PERFECT_SCORE = 300;
export const GOOD_SCORE = 100;
export const OK_SCORE = 50;
export const MISS_SCORE = 0;
export const COMBO_BONUS_PER_PERFECT = 5;

export const PLAYER_SIZE = 28;
// Pixels of horizontal travel per beat. Pads use this magnitude with the
// renderer applying a sign to position them on the RIGHT of PLAYER_X (future
// pads scrolling left) and the LEFT of PLAYER_X (past pads scrolling
// off-screen). 170 was tuned so a full 960-px canvas covers ~5.6 beats of
// approach time at 1:1 scale — comfortable preview without making fast
// charts feel sluggish.
export const SCROLL_SPEED_PX_PER_BEAT = 170;

export const PLAYFIELD_WIDTH = 960;
export const PLAYFIELD_HEIGHT = 540;
export const FLOOR_Y = PLAYFIELD_HEIGHT - 80;
export const CEILING_Y = 80;

export const FLIP_TRANSITION_BEATS = 0.18;

/**
 * HP-based fail model (replaces 3-heart system). Player runs from 0 to
 * MAX_HP = 100; events change HP and a drop to 0 ends the run as
 * "drained". Calibrated so an attentive player gains HP via on-beat hits
 * (perfect or good) faster than they lose it from the occasional miss, but a sustained
 * run of misses or hazards drains them out.
 */
export const MAX_HP = 100;
export const STARTING_HP = 80;
export const HP_PERFECT = 1;
export const HP_GOOD = 1;
// OK hits give back nothing — the mercy band salvages score but doesn't
// reward HP, so a player squeaking by on 180ms taps still bleeds out
// from misses and recovers slowly via passive regen.
export const HP_OK = 0;
export const HP_EMPTY_TAP = -6;
export const HP_HAZARD = -20;

/**
 * Damage dealt for the Nth consecutive miss in a streak (index 0 = first
 * miss). The streak is reset on any non-miss pad resolution. Index past the
 * end of the table reuses the final entry, so a sustained miss-spam tops out
 * at -4 per pad rather than snowballing -6 forever. With this curve a "bad
 * bar" of 4 misses costs 6 + 5 + 4 + 4 = 19 HP, well under STARTING_HP=80
 * so a single rough patch is comfortably survivable.
 */
export const HP_MISS_DAMAGE_BY_STREAK_INDEX: readonly number[] = [-6, -5, -4];

/**
 * "No pad in sight" half-window used to waive the empty-tap HP penalty.
 * If no un-hit pad sits within this many beats of the input (in either
 * direction), the player is wiggling the key in dead air and we don't
 * chunk HP for it. Scaled to roughly match the on-screen lookahead so
 * "anywhere I can see a pad" is the rule of thumb.
 */
export const HP_EMPTY_TAP_NEARBY_BEATS = 4;

/**
 * Onboarding grace: empty-tap HP penalty is fully disabled until the
 * player has hit this many required pads (perfect or good) in the
 * current run. New players need to feel out the timing without bleeding
 * HP from every misfire — once they've nailed N hits, we trust them to
 * understand that taps cost something.
 */
export const HP_EMPTY_TAP_GRACE_HITS = 3;

/**
 * Lifeline rule: a single damage event that would drop HP to 0 or below
 * is floored at 1 instead, but only if the player was strictly above
 * this threshold before the hit. Stops "ambush" hazards from ending a
 * healthy run in one shot; the player still dies from a follow-up tick
 * or a sustained drain.
 *
 * The threshold is tuned against HP_HAZARD = -20 (the only single damage
 * event large enough to vault someone from a "healthy-ish" HP straight
 * to 0): with threshold=8, a -20 ambush from HP 9-20 floors at 1 instead
 * of killing. A higher threshold (e.g. 20) would make the rule a no-op
 * because the player simply wouldn't die in those cases anyway. The
 * miss-streak table peaks at -6 per pad on the first miss, so misses
 * alone never trip the lifeline in practice (HP 9 - 6 = 3, safe).
 */
export const LIFELINE_HP_THRESHOLD = 8;

/**
 * Passive HP regen during clean stretches. The player gains
 * HP_REGEN_PER_TICK every HP_REGEN_INTERVAL_BEATS while combo > 0 and at
 * least HP_REGEN_QUIET_BEATS have passed since the last miss. Tuned slow
 * enough that a struggling player can't cheese it (combo dies on every
 * miss anyway) but a recovering player who strings hits together can claw
 * back ground.
 */
export const HP_REGEN_PER_TICK = 1;
export const HP_REGEN_INTERVAL_BEATS = 4;
export const HP_REGEN_QUIET_BEATS = 4;

/**
 * Brief invuln window after a hazard hit so the player isn't repeatedly
 * chunked while crossing a long spike strip. Same idea as the old
 * heart-invuln but only applies to hazard damage now.
 *
 * Doubles as the replay-determinism ceiling on the per-frame engine advance
 * (see {@link AudioEngine.maxAdvanceBeatsPerFrame}): a single engine step
 * must not span this period, or Pulse's once-per-step hazard sampling would
 * diverge from the server's fine-grained re-sim. Lowering this value tightens
 * that ceiling automatically; raising it loosens it.
 */
export const HAZARD_INVULN_BEATS = 1.0;

// Player position in playfield units. Player sits at ~15% from the left and
// pads spawn off-screen RIGHT, scrolling LEFT toward the receptor. 140 leaves
// a ~4.8-beat lookahead in the visible canvas to the RIGHT of the player
// ((960-140) / 170 px-per-beat), with the small region to the LEFT used by
// "past" pads scrolling off.
export const PLAYER_X = 140;

/**
 * Burst chamber scoring (see {@link BurstChamber}). Per-tap reward is
 * intentionally small so a perfect-replay score is dominated by pad
 * judgements, not mash spam; the completion bonus rewards engaging with
 * the chamber at all (base) plus scales gently with effort (per tap).
 */
export const BURST_CHAMBER_TAP_SCORE = 25;
export const BURST_CHAMBER_BASE_BONUS = 100;
export const BURST_CHAMBER_BONUS_PER_TAP = 10;

/**
 * Anti-cheat: max clicks-per-second the engine accepts inside a burst
 * chamber. Taps arriving faster than this are silently dropped — they
 * award no score, don't bump the chamber's tap count (so the completion
 * bonus stays capped too), and don't emit a `burst_tap` event. Trained
 * human mashers sustain ~14 cps in short bursts and elite players
 * briefly spike higher; 20 cps stays above realistic peak play while
 * neutering autoclickers. Same anti-cheat motivation as
 * {@link SPINNER_MAX_RPS}, but applied as a drop-on-overflow rather
 * than a clamp because each tap is a discrete event.
 */
export const BURST_CHAMBER_MAX_CPS = 20;

/**
 * Single source of truth for the burst-chamber accent colour, used by
 * the renderer (band tint, MASH! text, progress bar) and the session
 * (completion bonus floating text). Keeping it here avoids a magic
 * hex string scattered across files.
 */
export const BURST_CHAMBER_COLOUR = '#ff8a3b';
export const BURST_CHAMBER_COLOUR_SOFT = '#ffd9b8';

/**
 * Circles-mode spinner scoring (see `engine/circles/core.ts`).
 *
 * Spinners reward the player for rotating the cursor around the
 * playfield centre during the spinner's beat span. Each captured
 * cursor sample contributes its angular delta against the previous
 * sample, and points scale with raw radians traversed — so a faster
 * spin banks more score in the same window.
 *
 * Score accumulates continuously while the player spins, locked to
 * the combo multiplier captured at spin-start so the client's
 * per-frame increments and the server's per-step batched replay
 * produce the same final state.score.
 *
 * `SPINNER_POINTS_PER_RADIAN` × `2π` ≈ 88 points per full rotation,
 * making a moderate 4-rotation spinner roughly equal to ~3 perfect
 * circles before combo multiplier — competitive with, but not
 * dominating, normal hits. The auto-resolution at spinner end
 * additionally banks the existing free `good` (100 base × multiplier).
 */
export const SPINNER_POINTS_PER_RADIAN = 14;

/**
 * Anti-cheat: cap the angular delta the engine accepts between two
 * consecutive cursor samples to {@link SPINNER_MAX_RPS} rotations
 * per second's worth of beats. A doctored replay claiming impossible
 * spin speeds gets clamped silently rather than rejected — the
 * client cannot grant itself more score than a physically plausible
 * input would.
 *
 * 10 rps is well above the ~7 rps human ceiling but leaves headroom
 * for extreme players + sub-frame timing wobble.
 */
export const SPINNER_MAX_RPS = 10;

/**
 * RPS at which spinner visual + audio feedback is considered "fully
 * saturated" — glow intensity, label scale, colour ramp peaks all
 * land at this value. Set at half the {@link SPINNER_MAX_RPS} cap so
 * the visual peak corresponds to a reasonably-fast spin (~5 rps)
 * rather than the "manipulated-replay cap" speed; players who hit
 * the cap see steady peaked feedback rather than nothing happening.
 */
export const SPINNER_INTENSITY_SATURATION_RPS = SPINNER_MAX_RPS / 2;

/**
 * Spinner clear bonus, awarded each time the player crosses a full
 * rotation threshold *beyond* the spinner's required rotations. Mirrors
 * osu!'s post-clear spinner bonus: once the player has satisfied the
 * spinner's minimum spin requirement, every additional full rotation
 * grants a flat reward independent of the per-radian accumulator. The
 * threshold is held in beats by the engine (so the bonus rolls in even
 * mid-spin once the player has cleared) — see
 * {@link SPINNER_BONUS_ROTATIONS_THRESHOLD} below.
 */
export const SPINNER_CLEAR_BONUS_PER_ROTATION = 1000;

/**
 * Number of full rotations a spinner must accumulate before clear-bonus
 * rotations start landing. Tuned so a moderate spinner (2 beats wide
 * at 120 BPM) is achievable for a casual spinner; players who keep
 * spinning beyond this threshold get the +1000 burst per full extra
 * rotation, which is competitive with a perfect circle hit.
 */
export const SPINNER_BONUS_ROTATIONS_THRESHOLD = 3;

/**
 * Circles-mode slider tracking + scoring (see `engine/circles/core.ts`).
 *
 * Sliders work as a sequence of independently-scoring components:
 *   - Head tap: a normal circle judgement (perfect/good/ok/miss).
 *   - Slider ticks: each tick beat awards a small score + combo +1 if
 *     the cursor is inside the follow circle AND the player is holding
 *     the tap button at that moment.
 *   - Repeat edges: same gating, slightly larger reward (the reverse
 *     arrow is a more visible event than a tick).
 *   - Tail: at the slider's `endBeat`, a juicy bonus + combo +1 if
 *     tracking is still valid.
 *
 * Forgiveness bias (~25% friendlier than osu!):
 *   - Follow-circle radius = 3.0 × the slider's hit-circle radius
 *     (osu uses 2.4×). Honours our 20-30% more forgiving philosophy.
 *   - Tracking loss does NOT break combo. A dropped slider mid-run
 *     just stops crediting ticks — the player keeps any combo they
 *     built up off the head + earlier ticks. osu would break combo
 *     on tracking loss; we'd rather sliders feel like rewards than
 *     punishments.
 *   - Tick / repeat / tail score values are higher than osu's so a
 *     well-tracked slider banks meaningful score, encouraging
 *     players to engage with the curve rather than just clicking
 *     the head.
 *
 * Replay determinism: tick / repeat / tail scoring requires the
 * client to capture per-frame `pressing` cursor samples (already
 * captured for spinners — we just extend the same channel with a
 * `pressing` flag). Replays without pressing samples credit only
 * the head verdict (current head-only behaviour stays intact).
 */
export const SLIDER_FOLLOW_RADIUS_MULTIPLIER = 3.0;
export const SLIDER_TICK_SCORE = 30;
export const SLIDER_REPEAT_SCORE = 50;
export const SLIDER_TAIL_SCORE = 100;
